import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Users, CheckCircle, ArrowLeft, Send, Upload, X, Image } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { API } from '../App';



const SubmitGroupPage = ({ user, onLogout, refreshUserData }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    facebook_url: '',
    member_count: '',
    cover_image: '',
    submitter_email: '',
    submitter_name: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'fr' 
        ? 'Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.'
        : 'Unsupported file type. Use JPG, PNG, GIF or WebP.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr'
        ? 'L\'image est trop grande. Maximum 5MB.'
        : 'Image is too large. Maximum 5MB.');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setImageUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/facebook-groups/upload-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, cover_image: response.data.image_url }));
      toast.success(language === 'fr' ? 'Image téléchargée !' : 'Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'fr' 
        ? 'Erreur lors du téléchargement de l\'image'
        : 'Error uploading image');
      setImagePreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, cover_image: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/facebook-groups/submit`, formData);
      setSubmitted(true);
      toast.success(language === 'fr' 
        ? 'Groupe soumis avec succès !' 
        : 'Group submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 
        (language === 'fr' ? 'Erreur lors de la soumission' : 'Error submitting group');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="pt-12 pb-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'fr' ? 'Merci !' : 'Thank you!'}
              </h2>
              <p className="text-gray-600 mb-8">
                {language === 'fr' 
                  ? 'Votre groupe a été soumis avec succès. Notre équipe va l\'examiner et vous recevrez une notification par courriel une fois approuvé.'
                  : 'Your group has been submitted successfully. Our team will review it and you will receive an email notification once approved.'}
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'fr' ? 'Retour' : 'Back'}
        </Button>

        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-brand-600" />
            </div>
            <CardTitle className="text-2xl">
              {language === 'fr' ? 'Soumettre un groupe Facebook' : 'Submit a Facebook Group'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Partagez votre communauté de billets avec nos utilisateurs'
                : 'Share your ticket community with our users'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {language === 'fr' ? 'Nom du groupe *' : 'Group name *'}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'fr' ? 'Ex: Billets Montreal - Concerts' : 'Ex: Montreal Tickets - Concerts'}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="facebook_url">
                  {language === 'fr' ? 'URL du groupe Facebook *' : 'Facebook group URL *'}
                </Label>
                <Input
                  id="facebook_url"
                  name="facebook_url"
                  type="url"
                  value={formData.facebook_url}
                  onChange={handleChange}
                  placeholder="https://facebook.com/groups/..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {language === 'fr' ? 'Description' : 'Description'}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={language === 'fr' 
                    ? 'Décrivez votre groupe en quelques mots...'
                    : 'Describe your group in a few words...'}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="member_count">
                    {language === 'fr' ? 'Nombre de membres' : 'Member count'}
                  </Label>
                  <Input
                    id="member_count"
                    name="member_count"
                    value={formData.member_count}
                    onChange={handleChange}
                    placeholder="Ex: 5K, 10K+"
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    {language === 'fr' ? 'Image de couverture' : 'Cover image'}
                  </Label>
                  
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="cover_image_upload"
                  />
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      {imageUploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading}
                      className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-brand-50 transition-colors cursor-pointer"
                    >
                      {imageUploading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
                      ) : (
                        <>
                          <Image className="h-6 w-6 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            {language === 'fr' ? 'Cliquez pour importer' : 'Click to upload'}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">
                  {language === 'fr' ? 'Vos coordonnées' : 'Your contact info'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="submitter_name">
                      {language === 'fr' ? 'Votre nom' : 'Your name'}
                    </Label>
                    <Input
                      id="submitter_name"
                      name="submitter_name"
                      value={formData.submitter_name}
                      onChange={handleChange}
                      placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="submitter_email">
                      {language === 'fr' ? 'Votre courriel *' : 'Your email *'}
                    </Label>
                    <Input
                      id="submitter_email"
                      name="submitter_email"
                      type="email"
                      value={formData.submitter_email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-brand-600 hover:bg-brand-700"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    {language === 'fr' ? 'Soumettre le groupe' : 'Submit group'}
                  </span>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                {language === 'fr' 
                  ? 'En soumettant, vous acceptez que votre groupe soit affiché sur notre plateforme.'
                  : 'By submitting, you agree that your group may be displayed on our platform.'}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubmitGroupPage;
